import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n'

export const SITE_URL = 'https://roofora.de'
/** B2B-Subunternehmer-Marktplatz-Subdomain (docs/tasks/active/features/b2b/04-seo-subdomain-deployment.md) -
 *  eigene Google-Search-Console-Property, braucht daher eigene canonical/hreflang-URLs statt
 *  SITE_URL. Ein Frontend-Build/Container bedient beide Domains (siehe Epic-README), die
 *  Unterscheidung passiert zur Laufzeit über [isB2bHost]. */
export const B2B_SITE_URL = 'https://b2b.roofora.de'
/** Shared social-share preview image (see scripts/generate-og-image.mjs) - one generic branded
 *  card reused across all pages for now rather than a per-page image, which would need real
 *  per-page artwork to be worth the complexity. */
export const OG_IMAGE_URL = `${SITE_URL}/og-image.png`

/** True when the app is being served from the B2B subdomain - drives both which landing page
 *  App.tsx renders at the root route and which SEO metadata/canonical useSeo below emits. No
 *  hostname available outside a browser (e.g. during scripts/prerender.mjs, which always runs
 *  against localhost) - defaults to false there, same as a normal roofora.de visit. */
export function isB2bHost(): boolean {
  return typeof window !== 'undefined' && window.location.hostname.startsWith('b2b.')
}

export function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

export function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`
  let el = document.head.querySelector<HTMLLinkElement>(selector)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    if (hreflang) el.setAttribute('hreflang', hreflang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/** Removes a previously upserted alternate link, if any - needed where a hreflang cluster isn't
 *  guaranteed to cover all SUPPORTED_LANGUAGES (e.g. a blog post with no translation in some
 *  language, see useBlogSeo.ts). Without this, a language with no real alternate would silently
 *  keep whichever stale tag was already in the document (e.g. index.html's static default
 *  homepage hreflang links), pointing search engines at the wrong page. */
export function removeLink(rel: string, hreflang: string) {
  document.head.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`)?.remove()
}

/** Shared by every per-page SEO hook (useLandingPageSeo/useBlogSeo/usePvRechnerSeo/...) that
 *  injects a JSON-LD `<script>` - `id` keys the tag so each hook can update/remove exactly its own
 *  block without clobbering another page's structured data (e.g. a landing page's FAQPage script
 *  next to its BreadcrumbList script). */
export function upsertJsonLd(id: string, payload: Record<string, unknown>) {
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.id = id
    el.type = 'application/ld+json'
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(payload)
}

export function removeJsonLd(id: string) {
  document.getElementById(id)?.remove()
}

export interface BreadcrumbItem {
  name: string
  url: string
}

/** Builds a schema.org BreadcrumbList payload - every public page below the homepage gets one
 *  (see docs/tasks/active/seo/seo-nach-recherche.md #7), the homepage itself doesn't need one (a single-item
 *  trail carries no information). */
export function breadcrumbJsonLd(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

/** Builds a schema.org WebApplication payload for one of the free calculator tools
 *  (PvSizingTool/PvKostenRechnerWidget) - `price: '0'` matches the CLAUDE.md-Pflicht that nothing
 *  on the platform advertises payment right now. Deliberately has no `aggregateRating`: inventing
 *  one would violate the explicit "keine erfundenen Bewertungen" rule from seo-next.md - only add
 *  that field once real, disclosed user ratings exist to back it. Shared by usePvRechnerSeo,
 *  usePvKostenRechnerSeo and useLandingPageSeo (pSEO-Story Task 07: every page embedding the
 *  calculator gets this schema, not just the two bespoke calculator pages). */
export function webApplicationJsonLd(name: string, description: string, url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name,
    description,
    url,
    applicationCategory: 'UtilitiesApplication',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  }
}

/** Builds a schema.org HowTo payload for the step-by-step guides (GitLab-Issues #143/#144) -
 *  shares the "no fabricated numbers/claims" spirit of webApplicationJsonLd above: `image` is only
 *  included per step when a real screenshot exists (see HowToStep.screenshot), never a placeholder
 *  graphic passed off as documentation of the actual UI. `totalTime`/`estimatedCost` are
 *  deliberately omitted - the ticket only asks for step content, not a fabricated duration. */
export function howToJsonLd(name: string, description: string, steps: { title: string; whatToDo: string; screenshot?: { src: string } }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.title,
      text: step.whatToDo,
      ...(step.screenshot ? { image: step.screenshot.src.startsWith('http') ? step.screenshot.src : `${SITE_URL}${step.screenshot.src}` } : {}),
    })),
  }
}

export function useSeo(lang: SupportedLanguage) {
  const { t, i18n } = useTranslation()

  useEffect(() => {
    document.documentElement.lang = lang

    const b2b = isB2bHost()
    const siteUrl = b2b ? B2B_SITE_URL : SITE_URL
    const title = t(b2b ? 'seo.b2bTitle' : 'seo.title')
    const description = t(b2b ? 'seo.b2bDescription' : 'seo.description')
    const keywords = t(b2b ? 'seo.b2bKeywords' : 'seo.keywords')
    const ogLocale = t('seo.ogLocale')
    // No trailing slash - App.tsx's RootRedirect/LangRoute fallback always navigate to `/${lang}`,
    // never `/${lang}/`. A previous trailing-slash mismatch here meant canonical/hreflang pointed
    // at a URL variant nothing ever actually links to (see
    // docs/tasks/active/seo/optmierung/02-canonical-hreflang-domain-mismatch.md).
    const canonicalUrl = `${siteUrl}/${lang}`

    document.title = title
    upsertMeta('name', 'description', description)
    upsertMeta('name', 'keywords', keywords)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('property', 'og:locale', ogLocale)
    upsertMeta('property', 'og:image', OG_IMAGE_URL)
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertMeta('name', 'twitter:image', OG_IMAGE_URL)

    upsertLink('canonical', canonicalUrl)
    for (const supported of SUPPORTED_LANGUAGES) {
      upsertLink('alternate', `${siteUrl}/${supported}`, supported)
    }
    upsertLink('alternate', `${siteUrl}/de`, 'x-default')
  }, [lang, t, i18n.resolvedLanguage])
}
